import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import { fetchBreederAvailability } from 'dpl/breeder_search_results/hooks/useBreederAvailability';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { FILTER_POSTAL_CODE, PAGE } from 'dpl/constants/query_params';
import { isMobileUA, flatten } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';

import { RESULTS_TYPES } from '../utils/constants';
import FartherBreedersBanner from './FartherBreedersBanner';
import SearchAlertsBanner from './search_alerts/SearchAlertsBanner';
import BreederScreeningBanner from './BreederScreeningBanner';

const IS_MOBILE = isMobileUA();

const RESULT_GROUP_SIZE = IS_MOBILE ? 4 : 8;

function flattenDeliveryMethods(deliveryMethods) {
  return flatten(Object.values(deliveryMethods || {})).map(
    method => method.name
  );
}

export function resultGroupViewProps(
  breedId,
  resultsType,
  result,
  position,
  page
) {
  let resultProps = {};
  if (resultsType === RESULTS_TYPES.PUPPIES) {
    resultProps = {
      dog_id: result.id,
      puppy_name: result.name,
      breeder_profile_id: result.breeder_profile.id,
      breed_id: breedId,
      miles_away: result.distance_in_miles,
      miles_away_ranking: result.ranking_distance_in_miles,
      transportation_options: flattenDeliveryMethods(
        result.breeder_profile.delivery_methods
      )
    };
  } else {
    // For breeder results, use the analytics props from the service
    const analyticsProps = result.distance_analytics_props || {};
    resultProps = {
      breeder_profile_id: result.id,
      breed_id: breedId,
      miles_away: result.distance_in_miles,
      miles_away_ranking: result.ranking_distance_in_miles,
      transportation_options: flattenDeliveryMethods(result.delivery_methods),
      availability: result.availability_description,
      miles_away_displayed: analyticsProps.miles_away_displayed,
      logistics_display_type: analyticsProps.logistics_display_type,
      availability_displayed: result.analytics_availability_info || {
        puppies: 0,
        upcoming_litters: 0,
        reviews: result.testimonials_count
      },
      reviews_score: result.percent_recommend
    };
  }

  return {
    ...resultProps,
    position,
    page_number: page
  };
}

// Breeder availability loads async per card, so the group-viewed event runs
// as a self-contained job: it gathers its own data (through the same
// react-query cache the cards render from, so neither side duplicates the
// other's requests), then fires with complete payloads. Snoozed breeders
// always have empty availability, so they skip the fetch like the card does.
export function sendResultGroupViewedEvent(
  queryClient,
  { breedId, resultsType, resultGroup, position, page, view, viewFeature }
) {
  const availabilities = resultGroup.map(result =>
    resultsType === RESULTS_TYPES.BREEDERS && !result.snoozed
      ? fetchBreederAvailability(queryClient, breedId, result.id)
      : Promise.resolve(null)
  );

  return Promise.all(availabilities).then(groupAvailabilities => {
    sendRudderstackEvent(ANALYTICS_EVENTS.SEARCH_RESULT_GROUP_VIEWED_BY_USER, {
      view,
      view_feature: viewFeature,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
      results: resultGroup.map((result, groupIdx) =>
        resultGroupViewProps(
          breedId,
          resultsType,
          { ...result, ...groupAvailabilities[groupIdx] },
          position + groupIdx,
          page
        )
      )
    });
  });
}

function SearchResultsItem({
  result,
  results,
  resultsType,
  position,
  positionOnPage,
  analyticsPageNumber,
  hideDistance,
  Component
}) {
  const breedId = useSelector(({ server }) => server.breedId);
  const queryClient = useQueryClient();

  const analyticsView =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
      : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

  const analyticsViewFeature =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEW_FEATURES.PUPPY_CARD
      : ANALYTICS_VIEW_FEATURES.BREEDER_CARD;

  const analytics = useMemo(
    () => ({
      page_number: analyticsPageNumber,
      position,
      view: analyticsView,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    }),
    [position, analyticsPageNumber, analyticsView]
  );

  const trackInView = useCallback(() => {
    if (positionOnPage % RESULT_GROUP_SIZE === 1) {
      const idx = results.indexOf(result);

      sendResultGroupViewedEvent(queryClient, {
        breedId,
        resultsType,
        resultGroup: results.slice(idx, idx + RESULT_GROUP_SIZE),
        position,
        page: analyticsPageNumber,
        view: analyticsView,
        viewFeature: analyticsViewFeature
      });
    }
  }, [
    results,
    positionOnPage,
    analyticsView,
    analyticsViewFeature,
    position,
    analyticsPageNumber,
    breedId,
    resultsType,
    queryClient
  ]);

  return (
    <Component
      result={result}
      analytics={analytics}
      hideDistance={hideDistance}
      trackInView={trackInView}
    />
  );
}

SearchResultsItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  results: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object.isRequired,
  resultsType: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  positionOnPage: PropTypes.number.isRequired,
  analyticsPageNumber: PropTypes.number.isRequired,
  hideDistance: PropTypes.bool.isRequired,
  Component: PropTypes.func.isRequired
};

export default function SearchResults({
  results,
  resultsType,
  Card,
  distanceInMiles,
  totalItemCount,
  className,
  analyticsPageNumber
}) {
  const {
    itemsPerPage,
    stateLander,
    citySlug,
    preappliedFilters: { stateSlug } = {}
  } = useSelector(({ server }) => server);

  const params = useSelector(({ queryParams }) => queryParams);
  const currentPage = Number(params[PAGE]) || 1;
  const filteredByLocation = Boolean(params[FILTER_POSTAL_CODE] || stateSlug);

  const isFirstPage = currentPage === 1;

  function getPosition(idx) {
    let position = idx + 1;
    if (currentPage > 1) {
      position += (currentPage - 1) * itemsPerPage;
    }

    return position;
  }

  return (
    <ul
      className={classnames('SearchResults SearchResults--v2 row', className)}
    >
      {results?.map((result, idx) => {
        const positionOnPage = idx + 1;

        const isFirstResult = idx === 0 && isFirstPage;
        const prevResult = results[idx - 1] || {};

        const resultStateSlug =
          resultsType === RESULTS_TYPES.PUPPIES
            ? result?.breeder_profile?.state_slug
            : result?.state_slug;

        const didStartInRange =
          isFirstResult || prevResult.distance_in_miles <= distanceInMiles;

        const isInRange = result.distance_in_miles <= distanceInMiles;
        const isInState = resultStateSlug === stateSlug;

        const didSwitchToOutOfRange =
          !stateLander && distanceInMiles && didStartInRange && !isInRange;

        const isDesktopPuppiesView =
          !IS_MOBILE && resultsType === RESULTS_TYPES.PUPPIES;

        // If there are less than 4 items, do not show.
        // For desktop puppy view only, show on third row instead of after 4 cards
        const shouldDisplayBreederScreeningBanner =
          isFirstPage &&
          (isDesktopPuppiesView ? totalItemCount >= 20 : totalItemCount >= 4) &&
          (isDesktopPuppiesView ? idx === 11 : idx === 3);

        // only hide if a distance limit is specified and it goes past that distance
        const shouldHideDistanceInPuppyCards = stateLander
          ? !isInState
          : !isInRange;

        const shouldDisplayFartherBreedersBanner =
          resultsType === RESULTS_TYPES.BREEDERS && filteredByLocation
            ? Boolean(
                !result.snoozed &&
                  !stateLander &&
                  !citySlug &&
                  distanceInMiles &&
                  didSwitchToOutOfRange
              )
            : false;

        const remainingResultsCount =
          totalItemCount - ((currentPage - 1) * itemsPerPage + idx);

        // If there are less than 10 items, show it after the last item
        // For desktop puppy view only, show on fifth row instead of after 10 cards
        const showSearchAlerts =
          ((isDesktopPuppiesView ? totalItemCount < 20 : totalItemCount < 10) &&
            idx === totalItemCount - 1) ||
          (isDesktopPuppiesView ? idx === 19 : idx === 9);

        const shouldDisplaySearchAlertsBanner = isFirstPage && showSearchAlerts;

        return (
          <Fragment key={result.id}>
            {shouldDisplayFartherBreedersBanner && (
              <li className="mb8 mb10-md col-12">
                <FartherBreedersBanner count={remainingResultsCount} />
              </li>
            )}
            <li
              className={classnames('mb10-md col-12 col-md-6 mb6 mb8-md', {
                'col-xl-3': resultsType === RESULTS_TYPES.PUPPIES
              })}
            >
              <SearchResultsItem
                result={result}
                resultsType={resultsType}
                results={results}
                position={getPosition(idx)}
                positionOnPage={positionOnPage}
                analyticsPageNumber={analyticsPageNumber}
                hideDistance={shouldHideDistanceInPuppyCards}
                Component={Card}
              />
            </li>
            {shouldDisplayBreederScreeningBanner && (
              <li className="mb6 mb8-md col-12">
                <BreederScreeningBanner />
              </li>
            )}
            {shouldDisplaySearchAlertsBanner && (
              <li className="mb12 col-12">
                <SearchAlertsBanner />
              </li>
            )}
          </Fragment>
        );
      })}
    </ul>
  );
}

SearchResults.propTypes = {
  className: PropTypes.string,
  results: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)).isRequired,
  Card: PropTypes.func.isRequired,
  distanceInMiles: PropTypes.number,
  totalItemCount: PropTypes.number,
  analyticsPageNumber: PropTypes.number.isRequired
};

SearchResults.defaultProps = {
  distanceInMiles: null,
  totalItemCount: 0,
  className: null
};
